import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { AppPage, AppCard } from '../../components/general';
import { UserAccountCompensation } from '../../models/userAccountCompensation';
import UserCompensationService from '../../services/userCompensationService';
import { Toast } from 'primereact/toast';
import { DataTable, TableDefinition, DataTableSearchCreateHeader } from '../../components/datatable';
import { Messages } from 'primereact/messages';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { ToBooleanYesNo, ToDate } from '../../utils/string-helper';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormCalendar, FormInputNumber, FormCheckbox } from '../../components/form';
import { filterOptions, filterTemplates } from '../../utils/data-table-lookups';

interface UserCompensationsProps {
  userId?: string;
  asPage?: boolean;
  onRefresh?: () => void;
}

// Extended interface to include Date objects for filtering
interface UserAccountCompensationWithDate extends UserAccountCompensation {
  startDateObj?: Date;
}

// Interface for the form data
interface UserCompensationFormData {
  userAccountCompensationId?: number;
  startDate?: Date;
  loCompPercent?: number;
  minCompDollar?: number;
  maxCompDollar?: number;
  isBorrowerPaidDefault?: boolean;
  [key: string]: unknown;
}

const UserCompensations = ({ userId, asPage = true, onRefresh }: UserCompensationsProps = {}): JSX.Element => {
  const errors = useRef<Messages>(null);
  const toast = useRef<Toast>(null);
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [compensations, setCompensations] = useState<UserAccountCompensationWithDate[]>([]);
  const [search, setSearch] = useState<string>('');
  const [editorDialogVisible, setEditorDialogVisible] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedCompensation, setSelectedCompensation] = useState<UserAccountCompensationWithDate | null>(null);

  // Custom confirmation dialog states
  const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
  const [confirmationData, setConfirmationData] = useState<UserAccountCompensation | null>(null);
  // Track what operation we're confirming
  const [confirmationOperation, setConfirmationOperation] = useState<'save' | 'delete'>('save');

  const userCompensationService = UserCompensationService.getInstance();

  const { user, isLoading } = useAuth();

  const validationSchema = yup.object({
    startDate: yup.date().nullable().required('As of Date is required'),
    loCompPercent: yup.number().nullable().required('LO Comp Percent is required'),
  });

  const {
    control,
    formState: { errors: formErrors },
    reset,
    handleSubmit,
  } = useForm<UserCompensationFormData>({
    resolver: yupResolver<any>(validationSchema),
    defaultValues: {
      startDate: undefined,
      loCompPercent: undefined,
      minCompDollar: undefined,
      maxCompDollar: undefined,
      isBorrowerPaidDefault: false,
    },
  });

  useEffect(() => {
    fetchCompensations();
  }, []);

  useEffect(() => {
    if (editMode && selectedCompensation) {
      const dateObj = selectedCompensation.startDate ? new Date(selectedCompensation.startDate) : undefined;

      reset({
        ...selectedCompensation,
        // Use the Date object rather than the string
        startDate: dateObj,
        loCompPercent: selectedCompensation.loCompPercent || undefined,
        isBorrowerPaidDefault: selectedCompensation.isBorrowerPaidDefault || false,
      });
    }
  }, [editMode, selectedCompensation, reset]);

  const fetchCompensations = (): void => {
    const fetchPromise = userId
      ? userCompensationService.getUserCompensations(userId)
      : userCompensationService.getCurrentUserCompensations();

    fetchPromise
      .then((results: any) => {
        // If results is a single object, convert it into an array for consistent processing
        const dataArray = Array.isArray(results) ? results : results.userId ? [results] : [];

        // Convert string dates to Date objects for proper filtering
        const processedResults = dataArray.map((comp: UserAccountCompensation) => ({
          ...comp,
          startDateObj: comp.startDate ? new Date(comp.startDate) : undefined,
        }));

        // Sort results by startDate in descending order (newest first)
        const sortedResults = [...processedResults].sort((a, b) => {
          if (!a.startDate) return 1;
          if (!b.startDate) return -1;
          return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
        });

        setCompensations(sortedResults);
      })
      .catch((error: Error) => showError(error.message));
  };

  const showError = (error: string) => {
    errors.current?.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const showSaveConfirmation = (formData: UserAccountCompensation) => {
    // Store the form data to be used when confirmed
    setConfirmationData(formData);
    // Set operation type to save
    setConfirmationOperation('save');
    // Show our custom confirm dialog
    setConfirmDialogVisible(true);
  };

  const handleConfirmAccept = () => {
    setConfirmDialogVisible(false);
    if (!confirmationData) return;

    if (confirmationOperation === 'save') {
      saveEditedCompensation(confirmationData);
    } else if (confirmationOperation === 'delete') {
      deleteCompensation(confirmationData);
    }
  };

  const handleConfirmReject = () => {
    setConfirmDialogVisible(false);
  };

  const onSubmitForm = (formData: UserCompensationFormData) => {
    // Convert Date object back to string format for API
    const apiFormData: UserAccountCompensation = {
      ...formData,
      // Format date as ISO string for the API
      startDate: formData.startDate ? formData.startDate.toISOString().split('T')[0] : undefined,
    };

    // Both edit and add mode use the same confirmation flow
    showSaveConfirmation(apiFormData);
  };

  const formatCompensationDate = (compensation: UserAccountCompensation): UserAccountCompensation => {
    // Create a shallow copy to avoid mutating the original object
    const formattedComp = { ...compensation };

    // Convert any date objects to ISO strings for the API
    if (formattedComp.startDate && typeof formattedComp.startDate === 'object') {
      formattedComp.startDate = (formattedComp.startDate as Date).toISOString();
    }

    return formattedComp;
  };

  const updateCompensation = (compensation: UserAccountCompensation) => {
    const formattedComp = formatCompensationDate(compensation);

    // Add the ID for updates
    formattedComp.userAccountCompensationId = selectedCompensation?.userAccountCompensationId;

    userCompensationService
      .update(formattedComp)
      .then(() => {
        // Update the local state without refetching
        const updatedData = compensations.map((comp) =>
          comp.userAccountCompensationId === formattedComp.userAccountCompensationId
            ? {
                ...formattedComp,
                startDateObj: formattedComp.startDate ? new Date(formattedComp.startDate) : undefined,
              }
            : comp
        );
        setCompensations(updatedData);

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Compensation updated successfully',
          life: 3000,
        });

        // Only close the dialog after successful API call
        setEditorDialogVisible(false);

        // Call onRefresh if provided
        if (onRefresh) {
          onRefresh();
        }
      })
      .catch((error: Error) => {
        console.error('Error updating compensation:', error);
        showError('Failed to update compensation. Please try again.');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const createCompensation = (compensation: UserAccountCompensation) => {
    const formattedComp = formatCompensationDate(compensation);

    userCompensationService
      .create(userId!, formattedComp)
      .then(() => {
        // Refresh compensation data to get the new record with its ID
        fetchCompensations();

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Compensation created successfully',
          life: 3000,
        });

        // Only close the dialog after successful API call
        setEditorDialogVisible(false);

        // Call onRefresh if provided
        if (onRefresh) {
          onRefresh();
        }
      })
      .catch((error: Error) => {
        console.error('Error creating compensation:', error);
        showError('Failed to create compensation. Please try again.');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const saveEditedCompensation = (compensation: UserAccountCompensation) => {
    setIsSaving(true);

    // Determine if this is an update or create operation
    const isUpdate = selectedCompensation?.userAccountCompensationId !== undefined;

    if (isUpdate) {
      updateCompensation(compensation);
    } else {
      createCompensation(compensation);
    }
  };

  const handleSave = () => {
    // This will trigger validation before calling onSubmitForm
    handleSubmit(onSubmitForm)();
  };

  const onEdit = (compensation: UserAccountCompensation): void => {
    // Set selected compensation and open dialog in edit mode
    setSelectedCompensation(compensation as UserAccountCompensationWithDate);
    setEditMode(true);
    setEditorDialogVisible(true);
  };

  const onDelete = (compensation: UserAccountCompensation) => {
    // Store the compensation data to be used when confirmed
    setConfirmationData(compensation);
    // Set operation type to delete
    setConfirmationOperation('delete');
    // Show our custom confirm dialog
    setConfirmDialogVisible(true);
  };

  const deleteCompensation = (compensation: UserAccountCompensation) => {
    if (!compensation.userAccountCompensationId) {
      showError('Cannot delete compensation: missing ID');
      return;
    }

    // Start deletion process
    setIsDeleting(true);

    userCompensationService
      .delete(userId!, compensation.userAccountCompensationId)
      .then(() => {
        // Show success message
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Compensation record deleted successfully',
          life: 3000,
        });

        // After successful delete, refresh data
        fetchCompensations();

        // If parent provided a refresh callback, call it
        if (onRefresh) onRefresh();
      })
      .catch((error: Error) => {
        console.error('Error deleting compensation:', error);
        showError('Failed to delete compensation. Please try again.');
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const actionTemplate = (compensation: UserAccountCompensation) => {
    // Check if the compensation date is strictly before today (not including today)
    const isPastDate = (() => {
      if (!compensation.startDate) return false;

      // Create date objects without time component
      const compDate = new Date(compensation.startDate);
      const today = new Date();

      // Reset time to midnight to compare dates only
      compDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Return true only if date is strictly before today
      return compDate < today;
    })();

    // Don't show action buttons for past dates
    if (isPastDate) {
      return (
        <i
          className="pi pi-lock text-color-secondary"
          style={{ fontSize: '0.875rem' }}
          title="Readonly due to being past"
        ></i>
      );
    }

    return (
      <div className="flex">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={() => onEdit(compensation)}
          tooltip="Edit compensation"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => onDelete(compensation)}
          tooltip="Delete compensation"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  const tableDef: TableDefinition<UserAccountCompensation> = {
    columns: [
      {
        field: 'startDateObj', // Use the Date object field for filtering
        header: 'Start Date',
        dataType: 'date', // Use date dataType for proper date filtering
        body: (compensation: UserAccountCompensationWithDate) => ToDate(compensation.startDate),
        filterOptions: filterOptions.date,
        filterTemplate: filterTemplates.date,
      },
      {
        field: 'loCompPercent',
        header: 'LO Comp Percent',
        dataType: 'numeric', // Use numeric dataType for proper numeric comparison
        body: (compensation: UserAccountCompensation) => {
          // Display the value as it is, e.g., 1.25%
          if (compensation.loCompPercent === undefined || compensation.loCompPercent === null) {
            return '';
          }
          // Format with 3 decimal places and add % sign without multiplying by 100
          return `${compensation.loCompPercent.toFixed(3)}%`;
        },
      },
      {
        field: 'minCompDollar',
        header: 'Min Comp Dollar',
        dataType: 'numeric',
        body: (compensation: UserAccountCompensation) => {
          if (compensation.minCompDollar === undefined || compensation.minCompDollar === null) {
            return '';
          }
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(compensation.minCompDollar);
        },
      },
      {
        field: 'maxCompDollar',
        header: 'Max Comp Dollar',
        dataType: 'numeric',
        body: (compensation: UserAccountCompensation) => {
          if (compensation.maxCompDollar === undefined || compensation.maxCompDollar === null) {
            return '';
          }
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(compensation.maxCompDollar);
        },
      },
      {
        field: 'isBorrowerPaidDefault',
        header: 'Borrower Paid Default',
        dataType: 'text',
        body: (compensation: UserAccountCompensation) => ToBooleanYesNo(compensation.isBorrowerPaidDefault),
      },
      {
        header: 'Actions',
        body: actionTemplate,
        dataType: 'none',
        sortable: false,
        filter: false,
      },
    ],
  };

  const renderContent = () => {
    if (isLoading || !user) {
      return (
        <div className="flex flex-column align-items-center justify-content-center gap-2">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
        </div>
      );
    }

    return (
      <>
        <Toast ref={toast} />
        <Messages ref={errors} />
        <ConfirmDialog />

        {/* Progress bar - only shown during loading or deleting */}
        {(isLoading || isDeleting) && <ProgressBar mode="indeterminate" style={{ height: '6px', marginBottom: '10px' }} />}

        {/* Custom confirmation dialog for modals */}
        <Dialog
          header={confirmationOperation === 'save' ? 'Confirm Changes' : 'Confirm Delete'}
          visible={confirmDialogVisible}
          onHide={handleConfirmReject}
          style={{ width: '350px' }}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button label="No" icon="pi pi-times" onClick={handleConfirmReject} className="p-button-text" />
              <Button label="Yes" icon="pi pi-check" onClick={handleConfirmAccept} autoFocus />
            </div>
          }
        >
          <div className="flex align-items-center gap-3">
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: 'var(--yellow-500)' }} />
            {confirmationOperation === 'save' ? (
              <span>Are you sure you want to save these changes?</span>
            ) : (
              <span>
                Are you sure you want to delete this compensation record
                {confirmationData?.startDate ? ` from ${ToDate(confirmationData.startDate)}` : ''}?
              </span>
            )}
          </div>
        </Dialog>

        <DataTable
          header={
            <DataTableSearchCreateHeader
              onCreate={() => {
                reset({
                  startDate: undefined,
                  loCompPercent: undefined,
                  minCompDollar: undefined,
                  maxCompDollar: undefined,
                  isBorrowerPaidDefault: false,
                });
                setEditMode(false);
                setEditorDialogVisible(true);
              }}
              onSearchChange={setSearch}
            />
          }
          tableDef={tableDef}
          data={compensations}
          globalFilterValue={search}
          loading={isLoading}
        />

        {editorDialogVisible && (
          <Dialog
            header={editMode ? 'Edit Compensation' : 'Add Compensation'}
            visible={editorDialogVisible}
            onHide={() => !isSaving && setEditorDialogVisible(false)}
            style={{ width: '35rem' }}
            closeOnEscape={!isSaving}
            closable={!isSaving}
            modal
            className="p-fluid"
            footer={
              <div className="flex justify-content-end">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  className="p-button-text"
                  onClick={() => !isSaving && setEditorDialogVisible(false)}
                  disabled={isSaving}
                />
                <Button
                  label="Save"
                  icon={isSaving ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
                  onClick={handleSave}
                  disabled={isSaving}
                />
              </div>
            }
          >
            {isSaving && (
              <div
                className="absolute flex align-items-center justify-content-center"
                style={{
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  zIndex: 1000,
                }}
              >
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="5" />
              </div>
            )}
            <div className="grow p-fluid p-input-filled">
              {/* Grid layout for side-by-side fields that collapse on small screens */}
              <div className="grid">
                <div className="col-12 md:col-6">
                  <FormCalendar
                    control={control}
                    errors={formErrors}
                    field="startDate"
                    label="As of Date"
                    minDate={new Date()}
                    showIcon
                  />
                </div>
                <div className="col-12 md:col-6">
                  <FormInputNumber
                    control={control}
                    errors={formErrors}
                    field="loCompPercent"
                    label="LO Comp Percent"
                    mode="decimal"
                    suffix="%"
                    minFractionDigits={3}
                    maxFractionDigits={3}
                  />
                </div>
              </div>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <FormInputNumber
                    control={control}
                    errors={formErrors}
                    field="minCompDollar"
                    label="Min Comp Dollar"
                    mode="currency"
                    currency="USD"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <FormInputNumber
                    control={control}
                    errors={formErrors}
                    field="maxCompDollar"
                    label="Max Comp Dollar"
                    mode="currency"
                    currency="USD"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                  />
                </div>
              </div>
              {/* Checkbox remains full width */}
              <FormCheckbox
                control={control}
                errors={formErrors}
                field="isBorrowerPaidDefault"
                label="Borrower Paid by Default"
              />
            </div>
          </Dialog>
        )}
      </>
    );
  };

  const content = renderContent();

  return asPage ? (
    <AppPage title="User Compensations" breadcrumbs="Admin - User Compensations">
      <AppCard>{content}</AppCard>
    </AppPage>
  ) : (
    content
  );
};

export default UserCompensations;
